import { gql } from '@apollo/client';
import { useCallback, useMemo } from 'react';
import {
  UseSchedules_DisplayFragment,
  UseSchedules_ScheduleFragment,
} from './__generated__/useSchedules.graphql';

export type ScheduleUpdateFailedReason = 'unexpected_error';

/**
 * Counterpart of `usePowerSchedules`: the same display sync state machine, but read
 * from the display's `schedule`.
 */
export type ScheduleState =
  | {
      kind: 'empty';
    }
  | { kind: 'schedule_ready'; schedule: UseSchedules_ScheduleFragment }
  | {
      kind: 'schedule_sync_planned';
      schedule?: UseSchedules_ScheduleFragment;
      jobId: string;
    }
  | {
      kind: 'schedule_syncing';
      schedule?: UseSchedules_ScheduleFragment;
      jobId: string;
      progress?: number;
    }
  | {
      kind: 'schedule_sync_failed';
      schedule?: UseSchedules_ScheduleFragment;
      reason: ScheduleUpdateFailedReason;
    }
  | { kind: 'schedule_out_of_sync'; schedule: UseSchedules_ScheduleFragment };

export function useSchedules() {
  const getScheduleState = useCallback((display: UseSchedules_DisplayFragment): ScheduleState => {
    if (display.schedule?.latestJob?.__typename === 'ScheduleUpdateInProgress') {
      return {
        kind: 'schedule_syncing',
        schedule: display.schedule.schedule ?? undefined,
        jobId: display.schedule.latestJob.id,
      };
    }

    if (display.schedule?.latestJob?.__typename === 'ScheduleUpdateRejected') {
      return {
        kind: 'schedule_sync_failed',
        schedule: display.schedule.schedule ?? undefined,
        reason: getScheduleSyncFailedReason(display.schedule.latestJob.rejectionCode),
      };
    }

    if (display.schedule?.schedule && !display.schedule.isSynced) {
      return {
        kind: 'schedule_out_of_sync',
        schedule: display.schedule.schedule,
      };
    }

    if (
      display.schedule?.schedule &&
      display.schedule.latestJob?.__typename === 'ScheduleUpdateCompleted'
    ) {
      return { kind: 'schedule_ready', schedule: display.schedule.schedule };
    }

    return { kind: 'empty' };
  }, []);

  return useMemo(
    () => ({
      getScheduleState,
    }),
    [getScheduleState],
  );
}

function getScheduleSyncFailedReason(rejectionCode: string): ScheduleUpdateFailedReason {
  switch (rejectionCode) {
    default:
      return 'unexpected_error';
  }
}

useSchedules.graphql = {
  fragments: {
    UseSchedules_schedule: gql`
      fragment UseSchedules_schedule on Schedule {
        id
        title
      }
    `,
    UseSchedules_display: gql`
      fragment UseSchedules_display on Display {
        id
        schedule {
          schedule {
            id
            ...UseSchedules_schedule
          }
          isSynced
          latestJob {
            __typename
            id
            scheduleId
            ... on ScheduleUpdateRejected {
              rejectionCode
            }
          }
        }
      }
    `,
  },
};
